import { assert } from "@/wab/shared/common";
import { extractParamsFromPagePath } from "@/wab/shared/core/components";
import {
  asCode,
  ExprCtx,
  getCodeExpressionWithFallback,
} from "@/wab/shared/core/exprs";
import { tryEvalExpr, TryEvalExprResult } from "@/wab/shared/eval";
import { Expr, PageHref } from "@/wab/shared/model/classes";
import { matchesPagePath } from "@plasmicapp/loader-react";

/**
 * Takes in a url template and params, and substitutes in the param values
 * to make a real url
 */
export function substituteUrlParams(
  template: string,
  params: Record<string, string>
) {
  let path = template;
  for (const [key, value] of Object.entries(params)) {
    if (path.includes(`[[${key}]]`)) {
      // Optional catchall params can be empty
      path = substitutePathFragment(path, `[[${key}]]`, value || "");
    } else if (path.includes(`[${key}]`)) {
      // if value is empty string, keep the placeholder so the path is still valid
      if (value) {
        if (key.startsWith("...")) {
          // For catchall, value is already a valid url fragment
          path = substitutePathFragment(path, `[${key}]`, value);
        } else {
          path = path.replace(`[${key}]`, encodeURIComponent(value));
        }
      }
    }
  }
  // Replace optional catchall that still exist with ""
  const remainingParams = extractParamsFromPagePath(path);
  for (const param of remainingParams) {
    if (param.startsWith("...") && path.includes(`[[${param}]]`)) {
      path = path.replace(`[[${param}]]`, "");
    }
  }
  return path;
}

function substitutePathFragment(
  template: string,
  marker: string,
  value: string
) {
  // Remove starting and ending `/` from value
  value = value.replace(/^\/|\/$/g, "");
  return template.replace(marker, value);
}

/**
 * If `lookup` URI does not match `pagePath`, returns `false`.
 * Otherwise, returns param values. Param values are always returned as
 * strings, even for catchall params. However, param keys start with
 * "..." in such cases. Example:
 *
 * `getMatchingPagePathParams("/hello/[...catchall]/[slug]", "/hello/a/b/c")`
 * returns `{ "...catchall": "a/b", "slug": "c" }`.
 */
export function getMatchingPagePathParams(
  pagePath: string,
  lookup: string
): Record<string, string> | false {
  const match = matchesPagePath(pagePath, lookup);
  if (!match) {
    return false;
  }

  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(match.params)) {
    if (Array.isArray(value)) {
      params[`...${key}`] = value.join("/");
    } else {
      params[key] = value;
    }
  }

  return params;
}

interface GetPageHrefPathProps {
  expr: PageHref;
  exprCtx: ExprCtx;
}

/**
 * Renders a PageHref as a URL: the page path with each param substituted, then
 * `?key=value` query entries, then `#fragment`, with every dynamic value
 * rendered by `renderValue`. Returns undefined for a dangling page ref.
 */
export function renderPageHrefUrl(
  expr: PageHref,
  renderValue: (value: Expr) => string
): string | undefined {
  const path = expr.page?.pageMeta?.path;
  if (path == null) {
    return undefined;
  }
  let url = path;
  for (const [key, value] of Object.entries(expr.params)) {
    const str = renderValue(value);
    url = url.replace(`[[${key}]]`, () => str).replace(`[${key}]`, () => str);
  }
  const queryEntries = Object.entries(expr.query || {});
  if (queryEntries.length > 0) {
    const qs = queryEntries
      .map(([key, value]) => `${encodeURIComponent(key)}=${renderValue(value)}`)
      .join("&");
    url += `?${qs}`;
  }
  if (expr.fragment != null) {
    url += `#${renderValue(expr.fragment)}`;
  }
  return url;
}

/**
 * Converts a PageHref expr to path/URL with.
 * Includes path params, query params, and fragment rendered as code.
 */
export function pageHrefPathToCode({
  expr,
  exprCtx,
}: GetPageHrefPathProps): string {
  const url = renderPageHrefUrl(
    expr,
    (value) =>
      "${" +
      getCodeExpressionWithFallback(asCode(value, exprCtx), exprCtx) +
      "}"
  );
  assert(url != null, "PageHref is expected to contain a page");
  return "(`" + url + "`)";
}

export interface EvalPageHrefProps {
  expr: PageHref;
  exprCtx: ExprCtx;
  canvasEnv: Record<string, any>;
}

export function evalPageHrefPath({
  expr,
  exprCtx,
  canvasEnv,
}: EvalPageHrefProps): TryEvalExprResult {
  const code = pageHrefPathToCode({ expr, exprCtx });
  return tryEvalExpr(code, canvasEnv);
}
